import React, { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, MenuItem, Switch, TextField, Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { colors, forms } from '../theme/designSystem';
import { schedulesService, ScheduleOptions } from '../services/schedulesService';
import {
  FinancialPatientDiscount, HealthPlanPlanOption, OptionItem, PatientHealthPlanLink, PERIODICITY_OPTIONS, patientFinancialService,
  SavePatientDiscountPayload,
} from '../services/patientFinancialService';

/* Modais dos blocos do painel do caixa (RN-543g): desconto programado, periodicidade e plano de
   saúde. Nada é apagado — inativar preserva o histórico. */

const apiError = (err: unknown, fallback: string): string => {
  const e = err as { response?: { data?: { description?: string; message?: string; errors?: { description?: string }[] } } };
  const d = e?.response?.data;
  return d?.description || d?.message || d?.errors?.[0]?.description || fallback;
};

const parseMoney = (raw: string): number => {
  const t = raw.trim();
  if (t === '') return NaN;
  const normalized = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t;
  return Number(normalized);
};
const formatMoney = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const titleSx = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: colors.primary, color: '#fff', padding: '1.25rem', fontSize: '1.4rem', fontWeight: 600 } as const;
const contentSx = { padding: '1.5rem !important', paddingTop: '1.5rem !important' } as const;
const actionsSx = { padding: '1rem 1.5rem', gap: 1 } as const;
const cancelSx = { textTransform: 'none', borderColor: colors.border, color: colors.textSecondary } as const;
const saveSx = { textTransform: 'none', fontWeight: 600, bgcolor: colors.primary, '&:hover': { bgcolor: colors.primaryHover }, boxShadow: 'none' } as const;

/* ---------------------------------------------------------------- desconto programado */
interface DiscountModalProps {
  open: boolean;
  patientId: string;
  editing: FinancialPatientDiscount | null;
  professionalTypeOptions: OptionItem[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}

export const DiscountModal: React.FC<DiscountModalProps> = ({ open, patientId, editing, professionalTypeOptions, onClose, onSaved }) => {
  const [options, setOptions] = useState<ScheduleOptions | null>(null);
  const [value, setValue] = useState('');
  const [serviceTypeId, setServiceTypeId] = useState('');
  const [professionalTypeId, setProfessionalTypeId] = useState('');
  const [professionalIds, setProfessionalIds] = useState<string[]>([]);
  const [teamId, setTeamId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [comments, setComments] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setValue(editing ? formatMoney(editing.value) : '');
    setServiceTypeId(editing?.serviceTypeId ?? '');
    setProfessionalTypeId(editing?.professionalTypeId ?? '');
    setProfessionalIds(editing?.professionalIds ?? []);
    setTeamId(editing?.teamId ?? '');
    setFrom(editing?.from ? editing.from.slice(0, 10) : new Date().toISOString().slice(0, 10));
    setTo(editing?.to ? editing.to.slice(0, 10) : '');
    setComments(editing?.comments ?? '');
    if (!options) {
      schedulesService.getOptions().then(setOptions).catch(() => setOptions({ professionals: [], serviceTypes: [], teams: [] } as unknown as ScheduleOptions));
    }
  }, [open, editing, options]);

  const amount = parseMoney(value);
  // Equipe é obrigatória (Hilton, 05/09): o desconto sempre nasce dentro de uma equipe
  const valid = !Number.isNaN(amount) && amount > 0 && teamId !== '' && (!from || !to || to >= from);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const payload: SavePatientDiscountPayload = {
        id: editing?.id ?? null,
        patientId,
        discount: Math.round(amount * 100) / 100,
        serviceTypeId: serviceTypeId || null,
        professionalTypeId: professionalTypeId || null,
        professionalIds,
        teamId: teamId || null,
        initialDate: from || null,
        finalDate: to || null,
        comments: comments.trim() || null,
      };
      await patientFinancialService.saveDiscount(payload);
      await onSaved();
      onClose();
    } catch (err) {
      setError(apiError(err, 'Não foi possível salvar o desconto.'));
    } finally {
      setSaving(false);
    }
  };

  // Cascata (Hilton, 05/09): Equipe → Tipo de profissional → Profissionais → Serviço. Cada nível
  // filtra o seguinte; campo vazio = "qualquer" (o motor do agendamento trata vazio como curinga).
  const allProfessionals = (options?.professionals ?? []).filter((p) => p.active || professionalIds.includes(p.id));
  const byTeam = teamId ? allProfessionals.filter((p) => (p.teamIds ?? []).includes(teamId)) : allProfessionals;
  const typeIdsInTeam = new Set(byTeam.map((p) => p.professionalTypeId).filter(Boolean) as string[]);
  const typeOptions = professionalTypeOptions.filter((t) => !teamId || typeIdsInTeam.has(t.id));
  const professionals = professionalTypeId ? byTeam.filter((p) => p.professionalTypeId === professionalTypeId) : byTeam;
  const selectedProfessionals = professionals.filter((p) => professionalIds.includes(p.id));
  // Opção "Todos": lista vazia = qualquer profissional (curinga do motor); o chip deixa isso visível
  const ALL_PROFESSIONALS = { id: '__all__', name: 'Todos (qualquer profissional)' } as (typeof professionals)[number];
  const professionalChoices = [ALL_PROFESSIONALS, ...professionals];
  const professionalValue = professionalIds.length === 0 ? [ALL_PROFESSIONALS] : selectedProfessionals;
  const serviceIdsOfSelected = new Set(selectedProfessionals.flatMap((p) => (p.services ?? []).map((sv) => sv.serviceTypeId)));
  // Serviço só recorta com UM profissional escolhido (o valor do serviço é por profissional)
  const serviceEnabled = selectedProfessionals.length === 1;
  const serviceOptions = (options?.serviceTypes ?? []).filter((sv) => serviceIdsOfSelected.size === 0 || serviceIdsOfSelected.has(sv.id));

  // Mudou um nível acima, limpa o que deixou de caber abaixo
  useEffect(() => {
    if (professionalTypeId && !typeOptions.some((t) => t.id === professionalTypeId)) setProfessionalTypeId('');
    const allowed = new Set(professionals.map((p) => p.id));
    if (professionalIds.some((id) => !allowed.has(id))) setProfessionalIds((ids) => ids.filter((id) => allowed.has(id)));
    if (serviceTypeId && (!serviceEnabled || !serviceOptions.some((sv) => sv.id === serviceTypeId))) setServiceTypeId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, professionalTypeId, professionalIds.join(','), options]);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', overflow: 'hidden' } }}>
      <DialogTitle sx={titleSx}>
        {editing ? 'Editar desconto programado' : 'Novo desconto programado'}
        <IconButton onClick={onClose} sx={{ color: '#fff', padding: '0.25rem' }} disabled={saving} aria-label="Fechar"><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={contentSx}>
        <Typography sx={{ fontSize: '0.85rem', color: colors.textSecondary, mb: 2 }}>
          Valor em reais abatido por atendimento, gravado na sessão no momento do agendamento. Recortes vazios valem para qualquer atendimento do paciente.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 1.5, rowGap: 2 }}>
          <TextField size="small" label="Desconto por atendimento (R$)" value={value} required
            onChange={(e) => setValue(e.target.value.replace(/[^0-9.,]/g, ''))}
            onBlur={() => { const n = parseMoney(value); if (!Number.isNaN(n)) setValue(formatMoney(n)); }}
            InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 12, inputMode: 'decimal' }} placeholder="0,00" sx={{ gridColumn: { sm: '1 / -1' }, maxWidth: { sm: 260 } }} />
          <TextField select size="small" label="1. Equipe" value={teamId} onChange={(e) => setTeamId(e.target.value)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} required
            helperText={teamId ? '' : 'Obrigatória: escolha a equipe para liberar os demais recortes.'}>
            <MenuItem value="" disabled>Selecione a equipe</MenuItem>
            {(options?.teams ?? []).map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="2. Tipo de profissional" value={professionalTypeId} onChange={(e) => setProfessionalTypeId(e.target.value)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} disabled={!teamId}>
            <MenuItem value="">Qualquer tipo</MenuItem>
            {typeOptions.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </TextField>
          <Autocomplete
            multiple size="small" sx={{ gridColumn: { sm: '1 / -1' } }} disabled={!teamId}
            options={professionalChoices}
            value={professionalValue}
            onChange={(_, v) => {
              const ids = v.map((p) => p.id).filter((id) => id !== '__all__');
              // Escolheu "Todos" por último (ou esvaziou) → volta a qualquer profissional
              const pickedAll = v.length > 0 && v[v.length - 1].id === '__all__';
              setProfessionalIds(pickedAll ? [] : ids);
            }}
            getOptionLabel={(p) => p.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            disableCloseOnSelect
            renderOption={(props, p, { selected }) => (
              <li {...props} key={p.id}><Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0 }} />{p.name}</li>
            )}
            renderInput={(params) => <TextField {...params} label="3. Profissionais" InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />}
          />
          <TextField select size="small" label="4. Serviço" value={serviceTypeId} onChange={(e) => setServiceTypeId(e.target.value)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}
            disabled={!teamId || !serviceEnabled} sx={{ gridColumn: { sm: '1 / -1' } }}
            helperText={serviceEnabled ? 'Só os serviços que este profissional atende.' : 'Escolha exatamente um profissional para recortar por serviço.'}>
            <MenuItem value="">Qualquer serviço</MenuItem>
            {serviceOptions.map((sv) => <MenuItem key={sv.id} value={sv.id}>{sv.name}</MenuItem>)}
          </TextField>
          <TextField size="small" type="date" label="Vigência de" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField size="small" type="date" label="Vigência até" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }}
            error={!!from && !!to && to < from} helperText={!!from && !!to && to < from ? 'Não pode ser anterior ao início.' : 'Vazio = sem fim.'} />
          <TextField size="small" label="Observação" value={comments} onChange={(e) => setComments(e.target.value)} multiline minRows={2}
            InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 500 }} sx={{ gridColumn: { sm: '1 / -1' } }} />
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={actionsSx}>
        <Button onClick={onClose} variant="outlined" disabled={saving} sx={cancelSx}>Cancelar</Button>
        <Button variant="contained" disabled={!valid || saving} onClick={save} sx={saveSx}>{saving ? 'Salvando…' : 'Salvar'}</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---------------------------------------------------------------- periodicidade */
interface PeriodicityModalProps { open: boolean; patientId: string; current: string | null; onClose: () => void; onSaved: () => Promise<void>; }

export const PeriodicityModal: React.FC<PeriodicityModalProps> = ({ open, patientId, current, onClose, onSaved }) => {
  const [period, setPeriod] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (open) { setPeriod(current ?? ''); setError(''); } }, [open, current]);

  const save = async () => {
    setSaving(true);
    setError('');
    try { await patientFinancialService.setPeriodicity(patientId, period || null); await onSaved(); onClose(); } catch (err) { setError(apiError(err, 'Não foi possível salvar a periodicidade.')); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', overflow: 'hidden' } }}>
      <DialogTitle sx={titleSx}>
        Periodicidade de pagamento
        <IconButton onClick={onClose} sx={{ color: '#fff', padding: '0.25rem' }} disabled={saving} aria-label="Fechar"><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={contentSx}>
        <Typography sx={{ fontSize: '0.85rem', color: colors.textSecondary, mb: 2 }}>Informativa: com que frequência o paciente costuma pagar. A escolha anterior fica no histórico.</Typography>
        <TextField select size="small" fullWidth label="Periodicidade" value={period} onChange={(e) => setPeriod(e.target.value)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
          <MenuItem value="">Não definida</MenuItem>
          {PERIODICITY_OPTIONS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
        </TextField>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={actionsSx}>
        <Button onClick={onClose} variant="outlined" disabled={saving} sx={cancelSx}>Cancelar</Button>
        <Button variant="contained" disabled={saving || (period || null) === (current ?? null)} onClick={save} sx={saveSx}>{saving ? 'Salvando…' : 'Salvar'}</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---------------------------------------------------------------- plano de saúde */
interface HealthPlanModalProps {
  open: boolean;
  patientId: string;
  planOptions: HealthPlanPlanOption[];
  operatorOptions: OptionItem[];
  /** Vínculo em edição; nulo = vínculo novo. */
  editing: PatientHealthPlanLink | null;
  /** Falso = o perfil não vê carteirinha nem titular (grupo "convênio", RN-112q). */
  dataVisible: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

const NEW_PLAN = '__new__';

// RN-411c: convênio → plano em cascata; plano novo só de convênio já cadastrado. Vários vínculos
// vigentes por paciente; na edição o plano é fixo e só os dados do vínculo mudam. Sem vigência
// aqui: a do acordo mora no convênio e no plano; a do paciente, na validade da carteirinha.
export const HealthPlanModal: React.FC<HealthPlanModalProps> = ({ open, patientId, planOptions, operatorOptions, editing, dataVisible, onClose, onSaved }) => {
  const [operatorId, setOperatorId] = useState('');
  const [planId, setPlanId] = useState('');
  const [planName, setPlanName] = useState('');
  const [refundable, setRefundable] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [cardValidUntil, setCardValidUntil] = useState('');
  const [holder, setHolder] = useState<'' | 'holder' | 'dependent'>('');
  const [holderName, setHolderName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setOperatorId(''); setPlanId(''); setPlanName(''); setError('');
    setRefundable(editing?.refundable ?? false);
    setCardNumber(editing?.cardNumber ?? '');
    setCardValidUntil(editing?.cardValidUntil?.slice(0, 10) ?? '');
    setHolder(editing?.isHolder === null || editing?.isHolder === undefined ? '' : editing.isHolder ? 'holder' : 'dependent');
    setHolderName(editing?.holderName ?? '');
  }, [open, editing]);

  const plansOfOperator = planOptions.filter((p) => p.operatorId === operatorId);
  const isNewPlan = planId === NEW_PLAN;
  const planChosen = editing !== null || (operatorId !== '' && (isNewPlan ? planName.trim().length >= 2 : planId !== ''));
  const holderOk = !dataVisible || holder !== 'dependent' || holderName.trim() !== '';
  const valid = planChosen && holderOk;
  const noCatalog = editing === null && operatorOptions.length === 0;

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await patientFinancialService.setHealthPlan({
        patientId,
        linkId: editing?.id ?? null,
        healthPlanId: editing || isNewPlan ? null : planId,
        newOperatorId: !editing && isNewPlan ? operatorId : null,
        newPlanName: !editing && isNewPlan ? planName.trim() : null,
        refundable,
        cardNumber: cardNumber.trim() || null,
        cardValidUntil: cardValidUntil || null,
        isHolder: holder === '' ? null : holder === 'holder',
        holderName: holder === 'dependent' ? holderName.trim() : null,
      });
      await onSaved();
      onClose();
    } catch (err) {
      setError(apiError(err, 'Não foi possível salvar o vínculo.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', overflow: 'hidden' } }}>
      <DialogTitle sx={titleSx}>
        {editing ? 'Editar convênio do paciente' : 'Vincular convênio'}
        <IconButton onClick={onClose} sx={{ color: '#fff', padding: '0.25rem' }} disabled={saving} aria-label="Fechar"><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={contentSx}>
        {noCatalog ? (
          <Alert severity="info">Nenhum convênio vigente cadastrado. O Administrador cadastra em Serviços e convênios → Convênios.</Alert>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: forms.fieldGapX, rowGap: forms.fieldGapY }}>
            {editing ? (
              <TextField size="small" fullWidth label="Convênio e plano" value={editing.name} disabled InputLabelProps={{ shrink: true }} sx={{ gridColumn: '1 / -1' }} />
            ) : (
              <>
                <TextField select size="small" fullWidth required label="Convênio" value={operatorId}
                  onChange={(e) => { setOperatorId(e.target.value); setPlanId(''); setPlanName(''); }}
                  InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
                  <MenuItem value="" disabled>Selecione</MenuItem>
                  {operatorOptions.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                </TextField>
                <TextField select size="small" fullWidth required label="Plano" value={planId} disabled={!operatorId}
                  onChange={(e) => setPlanId(e.target.value)}
                  InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
                  <MenuItem value="" disabled>{operatorId ? 'Selecione' : 'Escolha o convênio'}</MenuItem>
                  {plansOfOperator.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                  <MenuItem value={NEW_PLAN}>Outro plano (digitar)</MenuItem>
                </TextField>
                {isNewPlan && (
                  <TextField size="small" fullWidth required label="Nome do plano" value={planName} onChange={(e) => setPlanName(e.target.value)}
                    InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 150 }} placeholder="Ex.: Nacional Apartamento" sx={{ gridColumn: '1 / -1' }} />
                )}
              </>
            )}

            {dataVisible ? (
              <>
                <TextField size="small" fullWidth label="Carteirinha / matrícula" value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 30 }} />
                <TextField size="small" fullWidth type="date" label="Validade da carteirinha" value={cardValidUntil} onChange={(e) => setCardValidUntil(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField select size="small" fullWidth label="Titularidade" value={holder} onChange={(e) => setHolder(e.target.value as typeof holder)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
                  <MenuItem value="">Não informado</MenuItem>
                  <MenuItem value="holder">Titular</MenuItem>
                  <MenuItem value="dependent">Dependente</MenuItem>
                </TextField>
                <TextField size="small" fullWidth label="Nome do titular" value={holderName} onChange={(e) => setHolderName(e.target.value)} disabled={holder !== 'dependent'} required={holder === 'dependent'} InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 150 }} />
              </>
            ) : (
              <Alert severity="info" sx={{ gridColumn: '1 / -1' }}>Carteirinha e titular são restritos ao seu perfil. Os valores gravados são mantidos.</Alert>
            )}

            <Box sx={{ gridColumn: '1 / -1' }}>
              <FormControlLabel control={<Switch checked={refundable} onChange={(e) => setRefundable(e.target.checked)} />} label={<Typography sx={{ fontSize: '0.9rem' }}>Reembolsável (paciente paga e pede reembolso ao convênio)</Typography>} />
            </Box>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={actionsSx}>
        <Button onClick={onClose} variant="outlined" disabled={saving} sx={cancelSx}>Cancelar</Button>
        <Button variant="contained" disabled={!valid || saving || noCatalog} onClick={save} sx={saveSx}>{saving ? 'Salvando…' : 'Salvar'}</Button>
      </DialogActions>
    </Dialog>
  );
};
